<template>
  <div class="dq-emp-select position-relative" ref="root">
    <label v-if="label" class="dq-label">{{ label }}</label>

    <!-- Trigger -->
    <button
      ref="triggerRef"
      type="button"
      class="btn btn-outline-secondary w-100 d-flex justify-content-between align-items-center dq-employee-btn"
      :class="{ 'is-invalid': invalid }"
      @click="toggle"
      @keydown.down.prevent="openAndFocusList()"
      @keydown.up.prevent="openAndFocusList(true)"
      @keydown.enter.prevent="toggle"
      @keydown.esc.prevent="close"
      :disabled="disabled"
    >
      <span class="text-truncate">
        <template v-if="mode === 'single'">
          {{ singleLabel }}
        </template>

        <template v-else>
          {{ multiLabel }}
        </template>
      </span>

      <i class="ti ti-chevron-down"></i>
    </button>

    <!-- Dropdown -->
    <div v-if="open" class="dq-dropdown shadow" role="listbox">
      <!-- Search -->
      <input
        ref="searchRef"
        type="text"
        class="form-control mb-2 dq-search"
        :placeholder="searchPlaceholder"
        v-model="query"
        @keydown.down.prevent="move(1)"
        @keydown.up.prevent="move(-1)"
        @keydown.enter.prevent="pickActive()"
        @keydown.esc.prevent="close"
        @keydown.tab="close"
      />

      <!-- List -->
      <div class="dq-dropdown-list" ref="listRef" tabindex="0"
           @keydown.down.prevent="move(1)"
           @keydown.up.prevent="move(-1)"
           @keydown.enter.prevent="pickActive()"
           @keydown.esc.prevent="close"
      >
        <template v-if="filtered.length">
          <div
            v-for="(emp, i) in filtered"
            :key="emp.id"
            class="dq-dropdown-item"
            :class="{ active: i === activeIndex }"
            @mouseenter="activeIndex = i"
            @mousedown.prevent="pick(emp)"
          >
            <!-- Checkbox for multi mode -->
            <input
              v-if="mode === 'multi'"
              type="checkbox"
              class="form-check-input me-2"
              :checked="isSelected(emp.id)"
              @change.stop
              @click.stop
            />

            <div class="dq-emp-row">
              <div class="dq-emp-avatar">
                <DqAvatar
                  :src="emp.avatar_url || ''"
                  :name="emp.name"
                  size-class="avatar-md"
                  shape-class="rounded-circle"
                />
              </div>
              <div class="dq-emp-meta">
                <div class="dq-emp-name">
                  {{ emp.name }}
                  <span class="dq-emp-id">• {{ emp.employee_id }}</span>
                </div>
                <div class="dq-emp-sub">
                  <span v-if="emp.department" class="dq-sub-pill">
                    <i class="ti ti-building"></i> {{ emp.department }}
                  </span>
                  <span v-if="emp.designation" class="dq-sub-pill">
                    <i class="ti ti-briefcase"></i> {{ emp.designation }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="mode === 'single' && isSelected(emp.id)" class="dq-selected-mark">
              <i class="ti ti-check"></i>
            </div>
          </div>
        </template>

        <div v-else class="text-muted text-center py-3">
          No employees found
        </div>
      </div>

      <!-- Footer small hint -->
      <div class="dq-hint">
        <span>↑ ↓ navigate</span> • <span>Enter select</span> • <span>Esc close</span>
      </div>
    </div>

    <!-- Selected pills (multi only) -->
    <div v-if="mode === 'multi' && selectedPreview.length" class="dq-selected-pills mt-2">
      <span v-for="emp in selectedPreview" :key="emp.id" class="dq-pill">
        {{ emp.name }}
        <i class="ti ti-x ms-1" @click="remove(emp.id)"></i>
      </span>
    </div>

    <div v-if="help" class="dq-help">{{ help }}</div>
    <div v-if="error" class="dq-error">{{ error }}</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from "vue";
import DqAvatar from "@/components/shared/DqAvatar.vue";

defineOptions({ name: "EmployeeSelectDropdown" });

const props = defineProps({
  label: { type: String, default: "Employees" },
  help: { type: String, default: "" },
  error: { type: String, default: "" },
  invalid: { type: Boolean, default: false },

  disabled: { type: Boolean, default: false },

  // "multi" or "single"
  mode: { type: String, default: "multi" },

  // employee list shape expected:
  // { id, name, employee_id, avatar_url, department, designation }
  employees: { type: Array, default: () => [] },

  // v-model for multi: array of ids, for single: id or null
  modelValue: { type: [Array, Number, String, null], default: null },

  placeholder: { type: String, default: "Select employees" },
  allLabel: { type: String, default: "All Employees" },
  searchPlaceholder: { type: String, default: "Search employee..." },

  // if you want to show "All Employees" in the trigger when multi mode and empty
  treatEmptyAsAll: { type: Boolean, default: false },

  fallbackAvatar: { type: String, default: "default.jpg" },
});

const emit = defineEmits(["update:modelValue", "change", "open", "close"]);

const root = ref(null);
const triggerRef = ref(null);
const searchRef = ref(null);
const listRef = ref(null);

const open = ref(false);
const query = ref("");
const activeIndex = ref(0);

const normalizedEmployees = computed(() =>
  (props.employees || []).map((e) => ({
    id: Number(e.id),
    name: e.name || "",
    employee_id: e.employee_id || "",
    avatar_url: e.avatar_url,
    department: e.department || "",
    designation: e.designation || "",
    _search: `${e.name || ""} ${e.employee_id || ""} ${e.department || ""} ${e.designation || ""}`.toLowerCase(),
  }))
);

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase();
  const list = normalizedEmployees.value;
  if (!q) return list;
  return list.filter((e) => e._search.includes(q));
});

watch(filtered, () => {
  activeIndex.value = 0;
});

function isSelected(id) {
  if (props.mode === "single") {
    return Number(props.modelValue) === Number(id);
  }
  const arr = Array.isArray(props.modelValue) ? props.modelValue : [];
  return arr.map(Number).includes(Number(id));
}

const selectedPreview = computed(() => {
  if (props.mode !== "multi") return [];
  const arr = Array.isArray(props.modelValue) ? props.modelValue.map(Number) : [];
  const map = new Map(normalizedEmployees.value.map((e) => [e.id, e]));
  return arr.map((id) => map.get(id)).filter(Boolean);
});

const multiLabel = computed(() => {
  if (props.disabled) return props.placeholder;

  const arr = Array.isArray(props.modelValue) ? props.modelValue : [];
  if (!arr.length) {
    return props.treatEmptyAsAll ? props.allLabel : props.placeholder;
  }
  return `${arr.length} selected`;
});

const singleLabel = computed(() => {
  const id = props.modelValue;
  if (id === null || id === undefined || id === "") return props.placeholder;
  const found = normalizedEmployees.value.find((e) => e.id === Number(id));
  return found ? found.name : props.placeholder;
});

function toggle() {
  if (open.value) close();
  else show();
}

function show() {
  if (props.disabled) return;
  open.value = true;
  emit("open");
  query.value = "";
  activeIndex.value = 0;
  nextTick(() => {
    searchRef.value?.focus();
  });
}

function close() {
  open.value = false;
  emit("close");
  query.value = "";
  activeIndex.value = 0;
}

function openAndFocusList(startFromEnd = false) {
  if (!open.value) show();
  nextTick(() => {
    if (startFromEnd) activeIndex.value = Math.max(filtered.value.length - 1, 0);
    listRef.value?.focus?.();
    ensureVisible();
  });
}

function move(delta) {
  const len = filtered.value.length;
  if (!len) return;
  let next = activeIndex.value + delta;
  if (next < 0) next = len - 1;
  if (next >= len) next = 0;
  activeIndex.value = next;
  ensureVisible();
}

function ensureVisible() {
  nextTick(() => {
    const container = listRef.value;
    if (!container) return;

    const items = container.querySelectorAll(".dq-dropdown-item");
    const el = items[activeIndex.value];
    if (!el) return;

    const cTop = container.scrollTop;
    const cBottom = cTop + container.clientHeight;
    const eTop = el.offsetTop;
    const eBottom = eTop + el.offsetHeight;

    if (eTop < cTop) container.scrollTop = eTop - 6;
    else if (eBottom > cBottom) container.scrollTop = eBottom - container.clientHeight + 6;
  });
}

function pickActive() {
  const emp = filtered.value[activeIndex.value];
  if (emp) pick(emp);
}

function pick(emp) {
  if (props.mode === "single") {
    emit("update:modelValue", emp.id);
    emit("change", emp.id);
    close();
    nextTick(() => triggerRef.value?.focus());
    return;
  }

  // multi
  const arr = Array.isArray(props.modelValue) ? props.modelValue.map(Number) : [];
  const id = Number(emp.id);
  const exists = arr.includes(id);
  const next = exists ? arr.filter((x) => x !== id) : [...arr, id];

  emit("update:modelValue", next);
  emit("change", next);

  // keep open for multi
  nextTick(() => searchRef.value?.focus());
}

function remove(id) {
  if (props.mode !== "multi") return;
  const arr = Array.isArray(props.modelValue) ? props.modelValue.map(Number) : [];
  const next = arr.filter((x) => x !== Number(id));
  emit("update:modelValue", next);
  emit("change", next);
}

// click outside
function onDocClick(e) {
  if (!open.value) return;
  if (!root.value) return;
  if (!root.value.contains(e.target)) close();
}

onMounted(() => document.addEventListener("mousedown", onDocClick));
onBeforeUnmount(() => document.removeEventListener("mousedown", onDocClick));
</script>

<style scoped>
.dq-label {
  font-weight: 800;
  font-size: 13px;
  color: rgba(15, 23, 42, 0.75);
}

.dq-help {
  margin-top: 6px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.55);
}

.dq-error {
  margin-top: 6px;
  font-size: 12px;
  color: #dc2626;
}

.dq-employee-btn {
  border-radius: 12px;
  padding: 10px 12px;
}

.dq-employee-btn.is-invalid {
  border-color: #dc2626 !important;
  box-shadow: 0 0 0 0.2rem rgba(220, 38, 38, 0.12) !important;
}

.dq-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  z-index: 1056;
  background: #fff;
  border-radius: 14px;
  border: 1px solid rgba(15, 23, 42, 0.08);
  padding: 10px;
}

.dq-search {
  border-radius: 12px;
}

.dq-dropdown-list {
  max-height: 260px;
  overflow-y: auto;
  outline: none;
}

.dq-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  border-radius: 12px;
  cursor: pointer;
  font-size: 14px;
  user-select: none;
  border: 1px solid transparent;
}

.dq-dropdown-item:hover {
  background: rgba(15, 23, 42, 0.05);
}

.dq-dropdown-item.active {
  background: rgba(59, 130, 246, 0.10);
  border-color: rgba(59, 130, 246, 0.25);
}

.dq-emp-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.dq-emp-avatar {
  width: 34px;
  height: 34px;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid rgba(15, 23, 42, 0.08);
  flex: 0 0 auto;
}

.dq-emp-avatar :deep(.dq-avatar-shell) {
  width: 100%;
  height: 100%;
}

.dq-emp-meta {
  min-width: 0;
}

.dq-emp-name {
  font-weight: 800;
  line-height: 1.1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dq-emp-id {
  font-weight: 700;
  color: rgba(15, 23, 42, 0.55);
  font-size: 12px;
}

.dq-emp-sub {
  margin-top: 4px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.dq-sub-pill {
  font-size: 12px;
  color: rgba(15, 23, 42, 0.65);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.04);
  border: 1px solid rgba(15, 23, 42, 0.06);
}

.dq-selected-mark {
  color: #16a34a;
  font-weight: 900;
}

.dq-hint {
  margin-top: 10px;
  font-size: 12px;
  color: rgba(15, 23, 42, 0.55);
  display: flex;
  justify-content: center;
  gap: 10px;
}

.dq-selected-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.dq-pill {
  background: rgba(59, 130, 246, 0.1);
  color: #1d4ed8;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.dq-pill .ti-x {
  cursor: pointer;
  font-size: 12px;
}
</style>
